import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useSupersedeCounterReading } from '@/features/counter-readings/queries';
import type { CounterReading } from '@/features/counter-readings/types';
import { ApiError } from '@/lib/api';

interface SupersedeReadingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reading: CounterReading;
}

export function SupersedeReadingModal({
  open,
  onOpenChange,
  reading,
}: SupersedeReadingModalProps) {
  const { t } = useTranslation();
  const supersedeMut = useSupersedeCounterReading();

  const [valueStr, setValueStr] = useState('');
  const [readAtStr, setReadAtStr] = useState('');
  const [notes, setNotes] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValueStr(reading.value);
      setReadAtStr(reading.readAt.slice(0, 10));
      setNotes('');
      setErrorKey(null);
      supersedeMut.reset();
    }
  }, [open, reading, supersedeMut]);

  const value = Number.parseFloat(valueStr);
  const validValue = Number.isFinite(value);
  const submitDisabled =
    !validValue || notes.trim().length === 0 || supersedeMut.isPending;

  const handleSubmit = () => {
    if (!validValue) return;
    if (notes.trim().length === 0) return;
    setErrorKey(null);
    supersedeMut.mutate(
      {
        readingId: reading.id,
        input: {
          value,
          readAt: new Date(readAtStr).toISOString(),
          notes: notes.trim(),
        },
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => {
          setErrorKey(
            err instanceof ApiError ? err.key : 'counter.supersede.saveError',
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('counter.supersede.title')}</DialogTitle>
          <DialogDescription>
            {t('counter.supersede.description')}
          </DialogDescription>
        </DialogHeader>

        {errorKey && (
          <Alert variant="destructive">
            <AlertTitle>{t('counter.supersede.saveError')}</AlertTitle>
            <AlertDescription>
              {t(errorKey, { defaultValue: t('counter.supersede.saveError') })}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="supersede-value" className="text-sm font-medium">
              {t('counter.supersede.valueLabel')} ({reading.unit})
            </label>
            <Input
              id="supersede-value"
              type="number"
              step="0.01"
              min="0"
              value={valueStr}
              onChange={(e) => setValueStr(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="supersede-read-at" className="text-sm font-medium">
              {t('counter.supersede.readAtLabel')}
            </label>
            <Input
              id="supersede-read-at"
              type="date"
              value={readAtStr}
              onChange={(e) => setReadAtStr(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="supersede-notes" className="text-sm font-medium">
              {t('counter.supersede.notesLabel')}
              <span className="ml-1 text-red-700">*</span>
            </label>
            <Input
              id="supersede-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={supersedeMut.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {supersedeMut.isPending && (
              <Loader2 className="animate-spin" aria-hidden />
            )}
            {t('counter.supersede.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
